<script setup lang="ts">
// The population map: the H3 resolution-8 field at rail width, drawn from what
// the server counted. The fill is the contract's 3×3 bivariate grid — slope is
// the row, smoothed catch per trap the column — and the same grid doubles as
// the legend and the filter control: every swatch is a button that narrows the
// shared spec to that class's dissolved geometry. The geometry arrives in the
// payload; this component only projects and paints it.
import { computed, ref } from "vue";

import type { MapReviewPopulationView } from "@/type/api";

import {
	LEVEL_NAMES,
	POPULATION_BIV,
	SLOPE_NAMES,
	bivariateFill,
	classLabel,
	formatBreak,
} from "./palette";

const props = defineProps<{
	/** The population payload; null until the first read lands. */
	view: MapReviewPopulationView | null;
	/** The class currently filtering the spec, or null for none. */
	selectedClass: number | null;
	/** Whether the cells are already on the workbench map. The parent owns the
	 * layer; without it the toggle keeps its own reading. */
	onMap?: boolean;
}>();

const emit = defineEmits<{
	"select-class": [index: number];
	"send-to-map": [on: boolean];
}>();

// One map in the rail, so a fixed clip id is safe and the boundary and the cell
// layer share it.
const CLIP_ID = "population-map-clip";
const WIDTH = 420;
const PAD = 8;

type LonLat = [number, number];

interface Projection {
	width: number;
	height: number;
	x: (lon: number) => number;
	y: (lat: number) => number;
}

// An equirectangular projection scaled by the mid-latitude cosine: the same
// framing the preview uses, which keeps the service area's shape right at
// district scale without pulling in a tile library for a panel-sized drawing.
const projection = computed<Projection | null>(() => {
	const view = props.view;
	if (!view) return null;
	const points: LonLat[] = [];
	for (const polygon of view.boundary?.polygons ?? []) {
		for (const ring of polygon) points.push(...ring);
	}
	// A payload with no boundary still has cells to draw; the cell rings bound
	// the same field. The outline simply has nothing to trace.
	if (points.length === 0) {
		for (const cell of view.cells) points.push(...cell.boundary);
	}
	if (points.length === 0) return null;

	let lon0 = Number.POSITIVE_INFINITY;
	let lon1 = Number.NEGATIVE_INFINITY;
	let lat0 = Number.POSITIVE_INFINITY;
	let lat1 = Number.NEGATIVE_INFINITY;
	for (const [lon, lat] of points) {
		if (lon < lon0) lon0 = lon;
		if (lon > lon1) lon1 = lon;
		if (lat < lat0) lat0 = lat;
		if (lat > lat1) lat1 = lat;
	}
	const kx = Math.cos(((lat0 + lat1) / 2) * (Math.PI / 180));
	const spanX = (lon1 - lon0) * kx;
	const spanY = lat1 - lat0;
	if (!(spanX > 0) || !(spanY > 0)) return null;

	// One scale for both axes: `inner` pixels across the cosine-corrected span.
	// Dividing each axis by its own span instead would stretch the field by
	// 1/cos(lat) and draw it past the viewBox, where the svg clips it.
	const inner = WIDTH - 2 * PAD;
	const scale = inner / spanX;
	return {
		width: WIDTH,
		height: Math.round(spanY * scale) + 2 * PAD,
		x: (lon: number) => PAD + (lon - lon0) * kx * scale,
		y: (lat: number) => PAD + (lat1 - lat) * scale,
	};
});

/** The district outline, one closed subpath per ring. Empty when the payload
 * carries no boundary; the cells still draw. */
const districtPath = computed(() => {
	const view = props.view;
	const project = projection.value;
	if (!view || !project) return "";
	let d = "";
	for (const polygon of view.boundary?.polygons ?? []) {
		for (const ring of polygon) {
			ring.forEach(([lon, lat], index) => {
				d += `${index === 0 ? "M" : "L"}${project.x(lon).toFixed(1)},${project.y(lat).toFixed(1)}`;
			});
			d += "Z";
		}
	}
	return d;
});

interface CellShape {
	key: string;
	points: string;
	fill: string;
	title: string;
}

const cells = computed<CellShape[]>(() => {
	const view = props.view;
	const project = projection.value;
	if (!view || !project) return [];
	return view.cells.map((cell) => ({
		key: cell.h3,
		points: cell.boundary
			.map(
				([lon, lat]) =>
					`${project.x(lon).toFixed(1)},${project.y(lat).toFixed(1)}`,
			)
			.join(" "),
		fill: bivariateFill(cell.class),
		title:
			cell.class == null
				? `${cell.h3} — no level or slope`
				: `${cell.h3} — ${classLabel(cell.class)}`,
	}));
});

interface StationDot {
	key: number;
	cx: number;
	cy: number;
	r: number;
	title: string;
}

const stations = computed<StationDot[]>(() => {
	const view = props.view;
	const project = projection.value;
	if (!view || !project) return [];
	return view.stations.map((station) => ({
		key: station.id,
		cx: project.x(station.lon),
		cy: project.y(station.lat),
		// Area, not radius, carries the sample weight, and the cap keeps a busy
		// station from swallowing the cells around it.
		r: 1.1 + Math.min(2.3, Math.sqrt(Math.max(0, station.samples)) / 13),
		title: `Station ${station.id} — ${station.samples} samples`,
	}));
});

/** How many cells the payload carries per class. A class with no cells cannot
 * narrow anything, so its swatch is inert rather than posting an empty
 * MultiPolygon the spec would refuse. While a class filter is active the
 * payload is that class's cells alone, which is why the other swatches are
 * inert until the active one is cleared. */
const classCounts = computed<number[]>(() => {
	const counts = new Array<number>(9).fill(0);
	for (const cell of props.view?.cells ?? []) {
		if (cell.class != null && cell.class >= 0 && cell.class <= 8) {
			counts[cell.class] += 1;
		}
	}
	return counts;
});

/** The slope axis, top row first, with the break values printed beside it. */
const legendRows = computed(() => {
	const [low, high] = props.view?.breaks.slope ?? [0, 0];
	return [
		{ slopeClass: 2, name: SLOPE_NAMES[2], range: `> ${formatBreak(high)}` },
		{
			slopeClass: 1,
			name: SLOPE_NAMES[1],
			range: `${formatBreak(low)} … ${formatBreak(high)}`,
		},
		{ slopeClass: 0, name: SLOPE_NAMES[0], range: `< ${formatBreak(low)}` },
	];
});

/** The level axis, left column first, with the break values printed beneath. */
const levelRanges = computed(() => {
	const [low, high] = props.view?.breaks.level ?? [0, 0];
	return [
		`< ${formatBreak(low)}`,
		`${formatBreak(low)} – ${formatBreak(high)}`,
		`> ${formatBreak(high)}`,
	];
});

// The toggle is the component's reading of the action; the parent owns the
// layer. When it passes its state back the label follows the map, and the emit
// is still the only way the layer changes.
const localOnMap = ref(false);
const mapOn = computed(() => props.onMap ?? localOnMap.value);

function toggleOnMap(): void {
	const next = !mapOn.value;
	localOnMap.value = next;
	emit("send-to-map", next);
}

function selectClass(index: number): void {
	emit("select-class", index);
}
</script>

<template>
	<section class="population-map">
		<header class="map-head">
			<p v-if="view" class="map-caption">
				H3 res {{ view.resolution }} · level × slope · week {{ view.lastWeek }}
			</p>
			<p v-else class="map-caption">Population map</p>
			<button
				type="button"
				class="map-send"
				:class="{ on: mapOn }"
				:aria-pressed="mapOn"
				:disabled="!view"
				@click="toggleOnMap"
			>
				{{ mapOn ? "Remove from map" : "Send to map" }}
			</button>
		</header>

		<p v-if="!view" class="map-empty">
			The population view has not loaded for the current filter.
		</p>

		<svg
			v-else-if="projection"
			class="map-svg"
			:viewBox="`0 0 ${projection.width} ${projection.height}`"
			role="img"
			aria-label="H3 cells filled by catch per trap and six-week slope"
		>
			<defs>
				<clipPath :id="CLIP_ID">
					<path :d="districtPath" clip-rule="evenodd" />
				</clipPath>
			</defs>
			<g :clip-path="`url(#${CLIP_ID})`">
				<polygon
					v-for="cell in cells"
					:key="cell.key"
					:points="cell.points"
					:fill="cell.fill"
					stroke="#ffffff"
					stroke-width="0.3"
				>
					<title>{{ cell.title }}</title>
				</polygon>
				<circle
					v-for="station in stations"
					:key="station.key"
					:cx="station.cx"
					:cy="station.cy"
					:r="station.r"
					fill="#1c1f23"
					opacity="0.65"
					stroke="#ffffff"
					stroke-width="0.35"
				>
					<title>{{ station.title }}</title>
				</circle>
			</g>
			<path
				v-if="districtPath"
				:d="districtPath"
				fill="none"
				fill-rule="evenodd"
				stroke="#1c1f23"
				stroke-width="1.3"
				stroke-linejoin="round"
			/>
		</svg>

		<p v-else class="map-empty">
			The payload carries no boundary or cell geometry to draw.
		</p>

		<div
			v-if="view"
			class="legend"
			aria-label="Bivariate class legend and filter"
		>
			<p class="legend-axis">slope, % per week — rising ↑</p>
			<div class="legend-grid">
				<span aria-hidden="true"></span>
				<span
					v-for="name in LEVEL_NAMES"
					:key="`column-${name}`"
					class="legend-column"
					>{{ name }}</span
				>
				<template v-for="row in legendRows" :key="row.slopeClass">
					<span class="legend-row">
						<span class="legend-name">{{ row.name }}</span>
						<span class="legend-range">{{ row.range }}</span>
					</span>
					<button
						v-for="(name, levelClass) in LEVEL_NAMES"
						:key="`${row.slopeClass}-${name}`"
						type="button"
						class="legend-swatch"
						:class="{
							selected: selectedClass === row.slopeClass * 3 + levelClass,
						}"
						:style="{ background: POPULATION_BIV[row.slopeClass][levelClass] }"
						:disabled="classCounts[row.slopeClass * 3 + levelClass] === 0"
						:title="
							classCounts[row.slopeClass * 3 + levelClass] === 0
								? `${classLabel(row.slopeClass * 3 + levelClass)} — no cells in scope`
								: classLabel(row.slopeClass * 3 + levelClass)
						"
						:aria-label="classLabel(row.slopeClass * 3 + levelClass)"
						:aria-pressed="selectedClass === row.slopeClass * 3 + levelClass"
						@click="selectClass(row.slopeClass * 3 + levelClass)"
					></button>
				</template>
			</div>
			<p class="legend-axis">catch per trap — high →</p>
			<div class="legend-breaks">
				<span v-for="label in levelRanges" :key="label">{{ label }}</span>
			</div>
			<p class="legend-note">
				Every swatch is a filter: click a class to narrow the spec to its cells,
				click it again to clear. Breaks are the latest week's terciles over the
				cells in scope.
			</p>
		</div>
	</section>
</template>

<style scoped>
.population-map {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
}

.map-head {
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin-bottom: 4px;
}

.map-caption {
	flex: 1 1 auto;
	min-width: 0;
	margin: 0;
	font-size: 0.7rem;
	color: #6c757d;
}

.map-send {
	flex: 0 0 auto;
	border: 1px solid #adb5bd;
	background: #fff;
	border-radius: 3px;
	padding: 1px 7px;
	font-size: 0.68rem;
	line-height: 1.5;
	color: #343a40;
	cursor: pointer;
}

.map-send:hover {
	border-color: #0d6efd;
	color: #0d6efd;
}

.map-send.on {
	border-color: #1864ab;
	background: #e7f5ff;
	color: #1864ab;
}

.map-send:disabled {
	border-color: #dee2e6;
	color: #adb5bd;
	cursor: default;
}

.map-svg {
	display: block;
	width: 100%;
	height: auto;
	max-height: 460px;
}

.map-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 140px;
	margin: 0;
	padding: 0 8px;
	text-align: center;
	font-size: 0.75rem;
	color: #adb5bd;
}

/* The legend is the control: the grid reads as slope × level, and every swatch
   is a button, so filter and key are the same object. */
.legend {
	margin-top: 8px;
}

.legend-axis {
	margin: 2px 0;
	font-size: 0.64rem;
	color: #6c757d;
}

.legend-grid {
	display: grid;
	grid-template-columns: minmax(0, 92px) repeat(3, minmax(0, 1fr));
	gap: 2px;
	align-items: stretch;
	width: 100%;
	max-width: 300px;
}

.legend-column {
	font-size: 0.62rem;
	color: #6c757d;
	text-align: center;
}

.legend-row {
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding-right: 6px;
	font-size: 0.64rem;
	line-height: 1.15;
	color: #6c757d;
	text-align: right;
}

.legend-name {
	color: #212529;
	font-size: 0.68rem;
}

.legend-range {
	font-size: 0.6rem;
	font-variant-numeric: tabular-nums;
}

.legend-swatch {
	height: 22px;
	border: 1px solid rgba(28, 31, 35, 0.12);
	border-radius: 2px;
	cursor: pointer;
}

.legend-swatch:hover {
	border-color: #1864ab;
}

.legend-swatch:disabled {
	opacity: 0.35;
	cursor: default;
}

.legend-swatch:disabled:hover {
	border-color: rgba(28, 31, 35, 0.12);
}

.legend-swatch.selected {
	outline: 2px solid #1864ab;
	outline-offset: 1px;
}

.legend-breaks {
	display: grid;
	grid-template-columns: minmax(0, 92px) repeat(3, minmax(0, 1fr));
	gap: 2px;
	max-width: 300px;
	font-size: 0.6rem;
	color: #6c757d;
	text-align: center;
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

.legend-breaks :first-child {
	grid-column: 2;
}

.legend-note {
	margin: 6px 0 0;
	font-size: 0.64rem;
	line-height: 1.4;
	color: #6c757d;
}
</style>
